import { useState, useCallback, useEffect } from "react";
import { Column, LocalView } from "../types";
import { getCurrentUserId } from "../../../utils/currentUser";

export const getViewsKey = (tableId: string): string =>
  `ram_column_options_views_${getCurrentUserId()}_${tableId}`;
export const getSelectedViewKey = (tableId: string): string =>
  `ram_column_options_selected_view_${getCurrentUserId()}_${tableId}`;

/**
 * Reconstitute Date objects from ISO strings when loading from localStorage.
 */
const hydrateColumns = (columns: Column[]): Column[] => {
  return columns.map((col) => {
    if (!col.filter) return col;
    if (col.filter.type === "date") {
      return {
        ...col,
        filter: {
          ...col.filter,
          date1: col.filter.date1 ? new Date(col.filter.date1 as any) : null,
          date2: col.filter.date2 ? new Date(col.filter.date2 as any) : null,
        },
      };
    }
    return col;
  });
};

const loadViews = (tableId: string): LocalView[] => {
  try {
    const raw = localStorage.getItem(getViewsKey(tableId));
    if (!raw) return [];
    const views: LocalView[] = JSON.parse(raw);
    return views.map((v) => ({
      ...v,
      columns: hydrateColumns(v.columns),
    }));
  } catch {
    return [];
  }
};

const saveViews = (tableId: string, views: LocalView[]) => {
  localStorage.setItem(getViewsKey(tableId), JSON.stringify(views));
};

const loadSelectedViewId = (tableId: string): string | null => {
  return localStorage.getItem(getSelectedViewKey(tableId));
};

const saveSelectedViewId = (tableId: string, viewId: string | null) => {
  if (viewId) {
    localStorage.setItem(getSelectedViewKey(tableId), viewId);
  } else {
    localStorage.removeItem(getSelectedViewKey(tableId));
  }
};

type UseLocalViewsProps = {
  tableId: string;
};

type UseLocalViewsReturn = {
  views: LocalView[];
  selectedView: LocalView | null;
  selectView: (id: string | null) => void;
  createView: (data: {
    name: string;
    description: string;
    isDefault: boolean;
    columns: Column[];
  }) => void;
  updateView: (data: {
    name: string;
    description: string;
    isDefault: boolean;
  }) => void;
  saveViewColumns: (columns: Column[]) => void;
  deleteView: () => void;
};

export const useLocalViews = ({
  tableId,
}: UseLocalViewsProps): UseLocalViewsReturn => {
  const [views, setViews] = useState<LocalView[]>(() => loadViews(tableId));
  const [selectedViewId, setSelectedViewId] = useState<string | null>(() => {
    const stored = loadSelectedViewId(tableId);
    if (stored) return stored;
    // Auto-select default view
    const allViews = loadViews(tableId);
    const defaultView = allViews.find((v) => v.isDefault);
    return defaultView?.id ?? null;
  });

  const selectedView =
    views.find((v) => v.id === selectedViewId) ?? null;

  // Persist views changes
  useEffect(() => {
    saveViews(tableId, views);
  }, [tableId, views]);

  // Persist selected view changes
  useEffect(() => {
    saveSelectedViewId(tableId, selectedViewId);
  }, [tableId, selectedViewId]);

  const selectView = useCallback((id: string | null) => {
    setSelectedViewId(id);
  }, []);

  const createView = useCallback(
    (data: {
      name: string;
      description: string;
      isDefault: boolean;
      columns: Column[];
    }) => {
      const newView: LocalView = {
        id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
        name: data.name,
        description: data.description,
        isDefault: data.isDefault,
        columns: data.columns,
        createdAt: new Date().toISOString(),
      };

      setViews((prev) => {
        let updated = [...prev, newView];
        // If this is the default, clear other defaults
        if (data.isDefault) {
          updated = updated.map((v) =>
            v.id === newView.id ? v : { ...v, isDefault: false }
          );
        }
        return updated;
      });

      setSelectedViewId(newView.id);
    },
    []
  );

  const updateView = useCallback(
    (data: { name: string; description: string; isDefault: boolean }) => {
      if (!selectedViewId) return;
      setViews((prev) => {
        let updated = prev.map((v) =>
          v.id === selectedViewId
            ? { ...v, name: data.name, description: data.description, isDefault: data.isDefault }
            : v
        );
        // If this is the default, clear other defaults
        if (data.isDefault) {
          updated = updated.map((v) =>
            v.id === selectedViewId ? v : { ...v, isDefault: false }
          );
        }
        return updated;
      });
    },
    [selectedViewId]
  );

  const saveViewColumns = useCallback(
    (columns: Column[]) => {
      if (!selectedViewId) return;
      setViews((prev) =>
        prev.map((v) =>
          v.id === selectedViewId ? { ...v, columns } : v
        )
      );
    },
    [selectedViewId]
  );

  const deleteView = useCallback(() => {
    if (!selectedViewId) return;
    setViews((prev) => prev.filter((v) => v.id !== selectedViewId));
    setSelectedViewId(null);
  }, [selectedViewId]);

  return {
    views,
    selectedView,
    selectView,
    createView,
    updateView,
    saveViewColumns,
    deleteView,
  };
};
